import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, DollarSign, Trash2, ExternalLink, Loader2, RefreshCw } from "lucide-react";

export default function AdminCampaignsList({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/campaigns", { headers: { "X-Admin-Token": token } });
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const action = async (cid, path, label, method = "post") => {
    setBusy((b) => ({ ...b, [cid]: label }));
    try {
      await api[method](path, null, { headers: { "X-Admin-Token": token } });
      toast.success(`${label} done`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${label} failed`);
    } finally {
      setBusy((b) => ({ ...b, [cid]: null }));
    }
  };

  return (
    <div className="border border-zinc-300 bg-white" data-testid="admin-campaigns-list">
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 flex-wrap gap-2">
        <div>
          <div className="label-eyebrow">Sponsor campaigns ($100 / 30-day donation)</div>
          <div className="font-display font-bold text-lg">{items.length} total</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={async () => {
              try {
                const { data } = await api.post("/admin/campaigns/run-renewal-reminders", null, {
                  headers: { "X-Admin-Token": token },
                });
                toast.success(`Renewals: ${data.sent} sent · ${data.skipped_already_reminded} skipped · ${data.failed} failed`);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Reminder run failed");
              }
            }}
            className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-zinc-700 hover:text-red-600 border border-zinc-300 px-3 py-1.5"
            data-testid="admin-campaigns-send-reminders"
          >
            Send renewal reminders
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                const { data } = await api.post("/admin/campaigns/run-winback-reminders", null, {
                  headers: { "X-Admin-Token": token },
                });
                toast.success(`Win-back: ${data.sent} sent · ${data.skipped_out_of_window} skipped · ${data.failed} failed`);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Win-back run failed");
              }
            }}
            className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-zinc-700 hover:text-red-600 border border-zinc-300 px-3 py-1.5"
            data-testid="admin-campaigns-send-winback"
          >
            Send win-back emails
          </button>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900"
            data-testid="admin-campaigns-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {loading && <div className="px-5 py-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>}

      {!loading && items.length === 0 && (
        <div className="px-5 py-12 text-center text-sm text-zinc-500" data-testid="admin-campaigns-empty">
          No advertiser campaigns yet.
        </div>
      )}

      <ul>
        {items.map((c) => (
          <li key={c.id} className="px-5 py-4 border-b border-zinc-200 grid grid-cols-1 lg:grid-cols-[80px_1fr_auto] gap-4 items-start" data-testid={`admin-campaign-${c.id}`}>
            <div className="aspect-square bg-zinc-100 border border-zinc-200 overflow-hidden">
              <img src={c.ad_image_url} alt="" className="w-full h-full object-cover"
                   onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={c.status} />
                <span className="font-mono text-[11px] text-zinc-500">{c.id}</span>
                {c.paid_through && (
                  <span className="font-mono text-[11px] text-zinc-700">
                    paid → {new Date(c.paid_through).toLocaleDateString()}
                  </span>
                )}
              </div>
              <div className="font-display font-bold mt-1 truncate">{c.ad_title}</div>
              <div className="text-sm">
                <strong>{c.advertiser_name}</strong>
                <span className="text-zinc-500"> · {c.contact_email}</span>
              </div>
              <a href={c.ad_link_url} target="_blank" rel="noreferrer"
                 className="text-xs text-red-600 hover:underline inline-flex items-center gap-1 mt-1 break-all">
                {c.ad_link_url} <ExternalLink size={11} />
              </a>
              <div className="text-[11px] font-mono text-zinc-400 mt-1">
                Owner: {c.owner_email} · Created {new Date(c.created_at).toLocaleString()}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {c.status !== "active" && c.status !== "rejected" && (
                <button
                  type="button"
                  disabled={!!busy[c.id]}
                  onClick={() => action(c.id, `/admin/campaigns/${c.id}/approve`, "Approve")}
                  className="inline-flex items-center gap-1 bg-zinc-900 text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-700 disabled:opacity-50"
                  data-testid={`admin-campaign-approve-${c.id}`}
                >
                  <CheckCircle2 size={12} /> Approve
                </button>
              )}
              <button
                type="button"
                disabled={!!busy[c.id]}
                onClick={() => action(c.id, `/admin/campaigns/${c.id}/mark-paid`, "Mark paid +30d")}
                className="inline-flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:bg-green-700 disabled:opacity-50"
                data-testid={`admin-campaign-mark-paid-${c.id}`}
              >
                <DollarSign size={12} /> Mark paid +30d
              </button>
              {c.status !== "rejected" && (
                <button
                  type="button"
                  disabled={!!busy[c.id]}
                  onClick={() => action(c.id, `/admin/campaigns/${c.id}/reject`, "Reject")}
                  className="inline-flex items-center gap-1 border border-red-600 text-red-700 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:bg-red-50 disabled:opacity-50"
                  data-testid={`admin-campaign-reject-${c.id}`}
                >
                  <XCircle size={12} /> Reject
                </button>
              )}
              <button
                type="button"
                disabled={!!busy[c.id]}
                onClick={() => {
                  if (!window.confirm("Permanently delete this campaign?")) return;
                  action(c.id, `/admin/campaigns/${c.id}`, "Delete", "delete");
                }}
                className="inline-flex items-center gap-1 text-zinc-500 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:text-red-700 disabled:opacity-50"
                data-testid={`admin-campaign-delete-${c.id}`}
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: ["bg-amber-200 text-zinc-900 border-amber-500", "Pending review"],
    pending_payment: ["bg-amber-200 text-zinc-900 border-amber-500", "Awaiting donation"],
    active: ["bg-green-500 text-white border-green-700", "Live"],
    paused: ["bg-zinc-300 text-zinc-900 border-zinc-500", "Paused (lapsed)"],
    rejected: ["bg-red-600 text-white border-red-700", "Rejected"],
  };
  const [cls, label] = map[status] || ["bg-zinc-100 text-zinc-900 border-zinc-300", status];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border ${cls}`}>
      {label}
    </span>
  );
}
